import React, { useEffect, useState, useRef } from 'react';
import { Layout, Row, Col, Card, Statistic, Typography, Divider, Spin, Button, DatePicker, message, Select, Table, Modal, Descriptions } from 'antd';
import { TrophyOutlined, ExclamationCircleOutlined, StarOutlined, DownloadOutlined, CalendarOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import '../Atoms/AdminDashboard.css';
import { fetchPointAllocatedByAwards, fetchMainBudgetData } from '../../Services/dataServices';
import dayjs, { Dayjs } from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const AdminDashboard = () => {
  const { instance, accounts } = useMsal();
  const [pointsAllocatedByAwards, setPointsAllocatedByAwards] = useState<any[]>([]);
  const [mainBudgetData, setMainBudgetData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(null);
  const [selectedNomineeCountry, setSelectedNomineeCountry] = useState<string[]>([]);
  const [selectedAwardName, setSelectedAwardName] = useState<string[]>([]);
  const [selectedApproval1Status, setSelectedApproval1Status] = useState<string[]>([]);

  const [selectedNomineeCity, setSelectedNomineeCity] = useState<string[]>([]);
  const chartRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  // Table view state
  const [showTable, setShowTable] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedNomineeCountry, selectedAwardName, selectedApproval1Status, selectedNomineeCity]);


  const fetchToken = async () => {
          const res = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0]
          });
          return res.idToken;
      };

  const getPointAllocatedByAwards = async (startDate: Date, endDate: Date) => {
    try {
      await fetchToken();
      //const budgetResponse = await fetchMainBudgetData(instance, accounts);
      //setMainBudgetData(budgetResponse?.apiResponse?.data);

      const response = await fetchPointAllocatedByAwards(instance, accounts, startDate, endDate);
      let data = response?.apiResponse?.data || [];
      setPointsAllocatedByAwards(data);
      console.log("Fetched points allocated by awards data:", data);
    }
    catch (error) {
      console.error("Error fetching point allocated by awards data:", error);
      message.error("Failed to fetch awards allocation data");
    }
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates) {
      const [start, end] = dates;
      
      // Validate that end date is greater than start date
      if (start && end) {
        // Check if both dates are from the same year
        if (start.year() !== end.year()) {
          message.error("Start date and end date must be from the same year");
          return;
        }
        
        if (end.isBefore(start)) {
          // If end date is before start date, don't update
          message.error("End date cannot be before start date");
          return;
        }
        if (end.isSame(start, 'day')) {
          // If both dates are the same, don't update
          message.warning("End date must be after start date");
          return;
        }
      }
      
      setStartDate(start);
      setEndDate(end);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleSearch = async () => {
    // Validate dates before searching
    if (!tempStartDate || !tempEndDate) {
      message.warning("Please select both start date and end date");
      return;
    }

    // Check if both dates are from the same year
    if (tempStartDate.year() !== tempEndDate.year()) {
      message.error("Start date and end date must be from the same year");
      return;
    }

    // Check if end date is after start date
    if (tempEndDate.isBefore(tempStartDate)) {
      message.error("End date cannot be before start date");
      return;
    }

    // Check if dates are the same
    if (tempEndDate.isSame(tempStartDate, 'day')) {
      message.warning("End date must be after start date");
      return;
    }

    // If all validations pass, update the actual dates and fetch data
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setLoading(true);
    setShowTable(true); // Show table view
    try {
      // Convert Dayjs to Date objects for the API call
      await getPointAllocatedByAwards(tempStartDate.toDate(), tempEndDate.toDate());
    } catch (error) {
      message.error("Failed to fetch data");
      console.error("Error in handleSearch:", error);
    } finally {
      setLoading(false);
    }
  };

  // Disable future dates (dates after today) for start date
  const disabledStartDate = (current: Dayjs) => {
    return current && current.isAfter(dayjs(), 'day');
  };

  // Disable dates for end date picker based on start date selection
  const disabledEndDate = (current: Dayjs) => {
    if (!current) return false;
    
    // Only apply start date restrictions if start date exists
    if (tempStartDate) {
      // Disable dates that are not in the same year as start date
      if (current.year() !== tempStartDate.year()) {
        return true;
      }
      
      // Disable dates that are before or same as start date
      if (current.isBefore(tempStartDate, 'day') || current.isSame(tempStartDate, 'day')) {
        return true;
      }
    }
    
    return false;
  };

  // Get unique nominee countries, award names, approval1 status, and cities
  const nomineeCountries = Array.from(new Set(pointsAllocatedByAwards.map(item => item.nomineeCountry).filter(Boolean)));
  const awardNames = Array.from(new Set(pointsAllocatedByAwards.map(item => item.awardName?.trim()).filter(Boolean)));
  const approval1Statuses = Array.from(new Set(pointsAllocatedByAwards.map(item => item.approval1Status).filter(Boolean)));
  const nomineeCities = Array.from(new Set(pointsAllocatedByAwards.map(item => item.nomineeCity).filter(Boolean)));

  // Filter data by all selected filters including date range
  const filteredData = pointsAllocatedByAwards.filter(item => {
    const nomineeCountryMatch = selectedNomineeCountry.length === 0 || selectedNomineeCountry.includes(item.nomineeCountry);
    const awardNameMatch = selectedAwardName.length === 0 || selectedAwardName.includes(item.awardName?.trim());
    const approval1StatusMatch = selectedApproval1Status.length === 0 || selectedApproval1Status.includes(item.approval1Status);
    const nomineeCityMatch = selectedNomineeCity.length === 0 || selectedNomineeCity.includes(item.nomineeCity);
    
    // Display all records from API regardless of date range
    return nomineeCountryMatch && awardNameMatch && approval1StatusMatch && nomineeCityMatch;
  });

  // Group awards by name and sum their amountInDollar
  const groupedPointsData = filteredData.reduce((acc: Map<string, number>, item: any) => {
    const awardName = item.awardName || `Award ${acc.size + 1}`;
    const amount = item.amountInDollar || 0;
    
    if (acc.has(awardName)) {
      acc.set(awardName, (acc.get(awardName) || 0) + amount);
    } else {
      acc.set(awardName, amount);
    }
    
    return acc;
  }, new Map<string, number>());

  // Convert grouped data to array format and sort by amount (highest to lowest)
  const groupedAwardsArray = Array.from(groupedPointsData.entries())
    .map(([awardName, amountInDollar]: [string, number]) => ({
      awardName,
      amountInDollar
    }))
    .sort((a, b) => (b.amountInDollar || 0) - (a.amountInDollar || 0));

  const totalAmountAllocated = filteredData.reduce((sum, item) => sum + (item.amountInDollar || 0), 0);

  // Prepare data for bar chart
  const chartData = groupedAwardsArray.map(item => ({
    name: item.awardName,
    amount: item.amountInDollar
  }));

  // Colors for the bar chart
  const chartColors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', 
    '#13c2c2', '#fa541c', '#eb2f96', '#a0d911', '#fadb14'
  ];

  // Download function for Excel export
  const downloadExcel = () => {
    if (filteredData.length === 0) {
      console.log("No data available to download");
      message.warning("No data to export based on current filters");
      return;
    }

    // Prepare date range information
    const dateRangeInfo = startDate && endDate 
      ? `Date Range: ${startDate.format('YYYY-MM-DD')} to ${endDate.format('YYYY-MM-DD')}`
      : 'Date Range: All Time';
    
    // Create filename with date range
    const fileName = startDate && endDate
      ? `Awards_Allocation_${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}.csv`
      : `Awards_Allocation_${new Date().getFullYear()}.csv`;

    // Create CSV content from the filtered data with all available fields
    const headers = [
      'Award Name',
      'Amount In $',
      'Core Value',
      'Nomination Date',
      'Rewarded Date',
      'Batch ID',
      'Approval 1 Status',
      'Approval 1 User',
      'Approval 1 Comments',
      'Approval 2 Status',
      'Approval 2 User',
      'Approval 2 Comments',
      'Approval ID',
      'Nominator Name',
      'Nominator Email',
      'Nominator Business Unit',
      'Nominator Citation',
      'Nominator City',
      'Nominator Country',
      'Nominator Department',
      'Nominator Designation',
      'Nominee Name',
      'Nominee Email',
      'Nominee Business Unit',
      'Nominee City',
      'Nominee Country',
      'Nominee Department',
      'Nominee Designation',
      'Team Name'
    ];
    
    // Helper function to escape CSV fields properly
    const escapeCsvField = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }
      // Convert to string and remove any existing quotes, newlines, and carriage returns
      const str = String(value).replace(/"/g, '""').replace(/\r?\n/g, ' ').replace(/\r/g, ' ');
      return `"${str}"`;
    };

    const csvContent = [
      dateRangeInfo,
      `Total Records: ${filteredData.length}`,
      `"Total Amount: $${totalAmountAllocated.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}"`,
      '', // Empty row for spacing
      headers.join(','),
      ...filteredData.map((item, index) => {
        // Format nomination date
        let nominationDate = '';
        if (item.nominationDate) {
          try {
            nominationDate = new Date(item.nominationDate).toLocaleDateString();
          } catch (e) {
            nominationDate = item.nominationDate;
          }
        }
        
        // Format rewarded date
        let rewardedDate = '';
        if (item.rewardedDate) {
          try {
            rewardedDate = new Date(item.rewardedDate).toLocaleDateString();
          } catch (e) {
            rewardedDate = item.rewardedDate;
          }
        }
        
        // Debug log for first item to verify field names
        if (index === 0) {
          console.log('Sample CSV row data:', {
            awardName: item.awardName,
            amountInDollar: item.amountInDollar,
            coreValue: item.coreValue,
            approval1Status: item.approval1Status,
            approval1User: item.approval1User,
            approval1Comments: item.approval1Comments
          });
        }
        
        return [
          escapeCsvField(item.awardName),
          escapeCsvField(item.amountInDollar),
          escapeCsvField(item.coreValue),
          escapeCsvField(nominationDate),
          escapeCsvField(rewardedDate),
          escapeCsvField(item.batchId),
          escapeCsvField(item.approval1Status),
          escapeCsvField(item.approval1User),
          escapeCsvField(item.approval1Comments),
          escapeCsvField(item.approval2Status),
          escapeCsvField(item.approval2User),
          escapeCsvField(item.approval2Comments),
          escapeCsvField(item.approvalId),
          escapeCsvField(item.nominatorName),
          escapeCsvField(item.nominatorEmail),
          escapeCsvField(item.nominatorBusinessUnitName),
          escapeCsvField(item.nominatorCitation),
          escapeCsvField(item.nominatorCity),
          escapeCsvField(item.nominatorCountry),
          escapeCsvField(item.nominatorDepartment),
          escapeCsvField(item.nominatorDesignation),
          escapeCsvField(item.nomineeName),
          escapeCsvField(item.nomineeEmail),
          escapeCsvField(item.nomineeBusinessUnit),
          escapeCsvField(item.nomineeCity),
          escapeCsvField(item.nomineeCountry),
          escapeCsvField(item.nomineeDepartment),
          escapeCsvField(item.nomineeDesignation),
          escapeCsvField(item.teamName)
        ].join(',');
      })
    ].join('\n');

    // Add UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Create and download the file
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success(`Excel file downloaded successfully! (${filteredData.length} records)`);
  };

  const handleViewDetails = (record: any) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
        <Content style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" tip="Loading dashboard data..." />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: 1600, margin: '0 auto' }}>

          {/* Date Range Filter */}
          <Row gutter={16} style={{ marginBottom: 10 }}>
            <Col span={24}>
              <Card>
                <Row gutter={16} align="middle" wrap={false}>
                  <Col>
                    <Text strong style={{ fontSize: '16px' }}>Start Date:</Text>
                  </Col>
                  <Col>
                    <DatePicker
                      value={tempStartDate}
                      onChange={(date) => setTempStartDate(date)}
                      format="YYYY-MM-DD"
                      style={{ width: 140 }}
                      disabledDate={disabledStartDate}
                      placeholder="Select start date"
                    />
                  </Col>
                  <Col>
                    <Text strong style={{ fontSize: '16px' }}>End Date:</Text>
                  </Col>
                  <Col>
                    <DatePicker
                      value={tempEndDate}
                      onChange={(date) => setTempEndDate(date)}
                      format="YYYY-MM-DD"
                      style={{ width: 140 }}
                      disabledDate={disabledEndDate}
                      placeholder="Select end date"
                    />
                  </Col>
                  <Col>
                    <Button 
                      type="primary" 
                      icon={<SearchOutlined />}
                      onClick={handleSearch}
                      style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                    >
                      Search
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={downloadExcel}
                      disabled={filteredData.length === 0}
                      style={{ 
                        backgroundColor: '#52c41a', 
                        borderColor: '#52c41a',
                        borderRadius: '6px',
                        boxShadow: '0 2px 6px rgba(82, 196, 26, 0.25)',
                        fontWeight: 500
                      }}
                    >
                      Excel
                    </Button>
                  </Col>
                  {showTable && (
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                      <div style={{ 
                        display: 'inline-block',
                        padding: '8px 16px', 
                        backgroundColor: '#f0f5ff', 
                        borderRadius: '6px',
                        border: '1px solid #adc6ff'
                      }}>
                        <Text type="secondary" style={{ fontSize: '12px', marginRight: 8 }}>
                          Amount:
                        </Text>
                        <Text strong style={{ fontSize: '15px', color: '#1890ff' }}>
                          ${totalAmountAllocated.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Text>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Filters */}
          {pointsAllocatedByAwards.length > 0 && (
            <Row gutter={16} style={{ marginBottom: 10 }}>
              <Col span={24}>
                <Card>
                  <Row gutter={[16, 16]}>
                    {/* First Row of Filters */}
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <div>
                        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                          Nominee Country:
                        </Text>
                        <Select
                          mode="multiple"
                          value={selectedNomineeCountry}
                          onChange={(value) => setSelectedNomineeCountry(value)}
                          style={{ width: '100%' }}
                          placeholder="Select nominee countries"
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                        >
                          {nomineeCountries.map((country) => (
                            <Option key={country} value={country}>
                              {country}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <div>
                        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                          Award Name:
                        </Text>
                        <Select
                          mode="multiple"
                          value={selectedAwardName}
                          onChange={(value) => setSelectedAwardName(value)}
                          style={{ width: '100%' }}
                          placeholder="Select award names"
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                        >
                          {awardNames.map((name) => (
                            <Option key={name} value={name}>
                              {name}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <div>
                        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                          Approval 1 Status:
                        </Text>
                        <Select
                          mode="multiple"
                          value={selectedApproval1Status}
                          onChange={(value) => setSelectedApproval1Status(value)}
                          style={{ width: '100%' }}
                          placeholder="Select approval statuses"
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                        >
                          {approval1Statuses.map((status) => (
                            <Option key={status} value={status}>
                              {status}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <div>
                        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                          Nominee City:
                        </Text>
                        <Select
                          mode="multiple"
                          value={selectedNomineeCity}
                          onChange={(value) => setSelectedNomineeCity(value)}
                          style={{ width: '100%' }}
                          placeholder="Select cities"
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          maxTagCount="responsive"
                        >
                          {nomineeCities.map((city) => (
                            <Option key={city} value={city}>
                              {city}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}

          {/* Table Display (replaces chart/cards on search) */}
          {showTable ? (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={24}>
                <Card 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong style={{ fontSize: '16px' }}>Awards Data</Text>
                      <div style={{ 
                        padding: '6px 12px', 
                        backgroundColor: '#e6f7ff', 
                        borderRadius: '4px',
                        border: '1px solid #91d5ff'
                      }}>
                        <Text type="secondary" style={{ fontSize: '12px', marginRight: 8 }}>
                          Showing:
                        </Text>
                        <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
                          {filteredData.length} record{filteredData.length !== 1 ? 's' : ''}
                        </Text>
                      </div>
                    </div>
                  }
                >
                  <Table
                    dataSource={filteredData.map((item, idx) => ({
                      key: idx,
                      awardName: item.awardName?.trim() || '',
                      amountInDollar: item.amountInDollar,
                      coreValue: item.coreValue,
                      nominationDate: item.nominationDate ? new Date(item.nominationDate).toLocaleDateString() : '',
                      nominationDateSort: item.nominationDate ? new Date(item.nominationDate).getTime() : 0,
                      rewardedDate: item.rewardedDate ? new Date(item.rewardedDate).toLocaleDateString() : '',
                      rewardedDateSort: item.rewardedDate ? new Date(item.rewardedDate).getTime() : 0,
                      batchId: item.batchId,
                      approval1Status: item.approval1Status,
                      approval1User: item.approval1User,
                      approval1Comments: item.approval1Comments,
                      approval2Status: item.approval2Status,
                      approval2User: item.approval2User,
                      approval2Comments: item.approval2Comments,
                      approvalId: item.approvalId,
                      nominatorName: item.nominatorName,
                      nominatorEmail: item.nominatorEmail,
                      nominatorBusinessUnitName: item.nominatorBusinessUnitName,
                      nominatorCitation: item.nominatorCitation,
                      nominatorCity: item.nominatorCity,
                      nominatorCountry: item.nominatorCountry,
                      nominatorDepartment: item.nominatorDepartment,
                      nominatorDesignation: item.nominatorDesignation,
                      nomineeName: item.nomineeName,
                      nomineeEmail: item.nomineeEmail,
                      nomineeBusinessUnit: item.nomineeBusinessUnit,
                      nomineeCity: item.nomineeCity,
                      nomineeCountry: item.nomineeCountry,
                      nomineeDepartment: item.nomineeDepartment,
                      nomineeDesignation: item.nomineeDesignation,
                      teamName: item.teamName
                    }))}
                    columns={[
                      { 
                        title: 'Award Name', 
                        dataIndex: 'awardName', 
                        key: 'awardName', 
                        width: 250, 
                        fixed: 'left', 
                        sorter: (a, b) => (a.awardName || '').localeCompare(b.awardName || ''),
                        render: (text) => (
                          <div style={{ 
                            wordWrap: 'break-word', 
                            wordBreak: 'break-word', 
                            whiteSpace: 'normal',
                            overflow: 'hidden'
                          }}>
                            {text || ''}
                          </div>
                        )
                      },
                      { 
                        title: 'Amount ($)', 
                        dataIndex: 'amountInDollar', 
                        key: 'amountInDollar', 
                        width: 150, 
                        render: (val) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{`$${(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</div>, 
                        sorter: (a, b) => (a.amountInDollar || 0) - (b.amountInDollar || 0) 
                      },
                      { 
                        title: 'Nomination Date', 
                        dataIndex: 'nominationDate', 
                        key: 'nominationDate', 
                        width: 150, 
                        sorter: (a, b) => (a.nominationDateSort || 0) - (b.nominationDateSort || 0),
                        render: (text) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text}</div>
                      },
                      { 
                        title: 'Rewarded Date', 
                        dataIndex: 'rewardedDate', 
                        key: 'rewardedDate', 
                        width: 150, 
                        sorter: (a, b) => (a.rewardedDateSort || 0) - (b.rewardedDateSort || 0),
                        render: (text) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text}</div>
                      },
                      { 
                        title: 'Batch ID', 
                        dataIndex: 'batchId', 
                        key: 'batchId', 
                        width: 200, 
                        sorter: (a, b) => (a.batchId || '').localeCompare(b.batchId || ''),
                        render: (text) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text || 'N/A'}</div>
                      },
                      { 
                        title: 'Approval ID', 
                        dataIndex: 'approvalId', 
                        key: 'approvalId', 
                        width: 150, 
                        sorter: (a, b) => (a.approvalId || '').localeCompare(b.approvalId || ''),
                        render: (text) => <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>{text}</div>
                      },
                      { 
                        title: 'Action', 
                        key: 'action', 
                        width: 100,
                        fixed: 'right',
                        render: (_, record) => (
                          <Button 
                            type="primary" 
                            icon={<EyeOutlined />} 
                            onClick={() => handleViewDetails(record)}
                            size="small"
                          >
                            View
                          </Button>
                        )
                      },
                    ]}
                    scroll={{ x: 1000 }}
                    bordered
                    pagination={{ 
                      current: currentPage,
                      pageSize: pageSize, 
                      showSizeChanger: true, 
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                      onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      }
                    }}
                  />
                </Card>
              </Col>
            </Row>
          ) : (
            <>
              {/* Bar Chart Display */}
              {groupedAwardsArray.length > 0 && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={24}>
                    <div ref={chartRef}>
                      <Card style={{ minHeight: 400 }}>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            data={chartData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              angle={-45}
                              textAnchor="end"
                              height={120}
                              interval={0}
                              style={{ fontSize: '12px' }}
                            />
                            <YAxis 
                              label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                              tickFormatter={(value) => `$${value.toLocaleString()}`}
                            />
                            <Tooltip 
                              formatter={(value: any) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Amount']}
                              contentStyle={{ backgroundColor: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                            />
                            <Legend />
                            <Bar dataKey="amount" name="Award Name" radius={[8, 8, 0, 0]}>
                              {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </div>
                  </Col>
                </Row>
              )}
              {/* Awards Allocation Display */}
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={24}>
                  <Card>
                    {groupedAwardsArray.length > 0 ? (
                      <div ref={cardsRef}>
                        {/* ...existing code for cards... */}
                        <Row gutter={[16, 16]}>
                          {groupedAwardsArray.map((award, index) => {
                            const maxAmount = Math.max(...groupedAwardsArray.map(item => item.amountInDollar || 0));
                            const colors = [
                              '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', 
                              '#13c2c2', '#fa541c', '#eb2f96', '#a0d911', '#fadb14'
                            ];
                            const cardColor = colors[index % colors.length];
                            return (
                              <Col xs={24} sm={12} lg={8} xl={6} key={index}>
                                {/* ...existing code for each card... */}
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '80px 20px',
                        color: '#8c8c8c'
                      }}>
                        <ExclamationCircleOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d9d9d9' }} />
                        <div>
                          <Title level={4} type="secondary">No Awards Data Available</Title>
                          <Text type="secondary">
                            Awards allocation data will appear here once awards are processed.
                          </Text>
                        </div>
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>

        {/* Modal for detailed view */}
        <Modal
          title="Award Details"
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={[
            <Button key="close" type="primary" onClick={handleModalClose}>
              Close
            </Button>
          ]}
          width={900}
          style={{ top: 20 }}
        >
          {selectedRecord && (
            <>
              <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Award Name" span={2}>
                {selectedRecord.awardName || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Amount ($)">
                ${(selectedRecord.amountInDollar || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Descriptions.Item>
              <Descriptions.Item label="Core Value">
                {selectedRecord.coreValue || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nomination Date">
                {selectedRecord.nominationDate || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Rewarded Date">
                {selectedRecord.rewardedDate || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Batch ID" span={2}>
                {selectedRecord.batchId || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Approval ID" span={2}>
                {selectedRecord.approvalId || 'N/A'}
              </Descriptions.Item>
              
              <Descriptions.Item label="Approval 1 Status">
                {selectedRecord.approval1Status || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Approval 1 User">
                {selectedRecord.approval1User || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Approval 1 Comments" span={2}>
                {selectedRecord.approval1Comments || 'N/A'}
              </Descriptions.Item>
              
              <Descriptions.Item label="Approval 2 Status">
                {selectedRecord.approval2Status || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Approval 2 User">
                {selectedRecord.approval2User || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Approval 2 Comments" span={2}>
                {selectedRecord.approval2Comments || 'N/A'}
              </Descriptions.Item>
              
              <Descriptions.Item label="Nominator Name">
                {selectedRecord.nominatorName || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominator Email">
                {selectedRecord.nominatorEmail || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominator Business Unit">
                {selectedRecord.nominatorBusinessUnitName || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominator City">
                {selectedRecord.nominatorCity || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominator Country">
                {selectedRecord.nominatorCountry || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominator Department">
                {selectedRecord.nominatorDepartment || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominator Designation">
                {selectedRecord.nominatorDesignation || 'N/A'}
              </Descriptions.Item>
              
              <Descriptions.Item label="Nominee Name">
                {selectedRecord.nomineeName || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominee Email">
                {selectedRecord.nomineeEmail || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominee Business Unit">
                {selectedRecord.nomineeBusinessUnit || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominee City">
                {selectedRecord.nomineeCity || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominee Country">
                {selectedRecord.nomineeCountry || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominee Department">
                {selectedRecord.nomineeDepartment || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Nominee Designation">
                {selectedRecord.nomineeDesignation || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Team Name">
                {selectedRecord.teamName || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
            
            <Divider style={{ margin: '16px 0' }} />
            
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>
                Nominator Citation:
              </Text>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {selectedRecord.nominatorCitation || 'N/A'}
              </div>
            </div>
            </>
          )}
        </Modal>
      </Content>
    </Layout>
  );
};

export default AdminDashboard;